/* eslint-disable no-console */
import { ApplicationInsightsService } from './application-insights.service';
import { CorrelationService } from './correlation.service';
import { environment } from '../../../environments/environment';
import { Injectable } from '@angular/core';
import { SeverityLevel } from '@microsoft/applicationinsights-common';

export enum LogLevel {
    Debug = 0,
    Info = 1,
    Warn = 2,
    Error = 3,
    Off = 4
}

export interface LogEntry {
    level: LogLevel;
    levelName: string;
    message: string;
    timestampUtc: string;
    correlationId?: string;
    data?: unknown[];
}

@Injectable({
    providedIn: 'root'
})
export class LoggerService {
    private readonly minLevel = this.parseLogLevel(environment.logLevel);
    private readonly logToConsole = environment.logToConsole ?? false;

    constructor(private readonly appInsights: ApplicationInsightsService,
                private correlationService: CorrelationService
    ) { }

    // Support standard console.log() usage.
    log(message?: any, ...optionalParams: any[]): void {
        if (message == null) {
            return;
        }

        if (this.logToConsole) console.log(message, ...optionalParams);
    }

    debug(message: string, ...data: unknown[]): void {
        this.write(LogLevel.Debug, 'DEBUG', message, data);
    }

    info(message: string, ...data: unknown[]): void {
        this.write(LogLevel.Info, 'INFO', message, data);
    }

    warn(message: string, ...data: unknown[]): void {
        this.write(LogLevel.Warn, 'WARN', message, data);
    }

    error(message: string, ...data: unknown[]): void {
        this.write(LogLevel.Error, 'ERROR', message, data);
    }

    private write(level: LogLevel, levelName: string, message: string, data?: unknown[]): void {
        if (!this.shouldLog(level)) { return; }

        const entry: LogEntry = {
            level,
            levelName,
            message,
            timestampUtc: new Date().toISOString(),
            data
        };

        if (this.logToConsole) this.writeToConsole(entry);
        this.writeToApplicationInsights(entry);
    }

    private writeToApplicationInsights(entry: LogEntry): void {
        const properties: Record<string, string> = {
            source: 'UI',
            logLevel: entry.levelName,
            timestampUtc: entry.timestampUtc
        };

        this.addDataToProperties(properties, entry.data);

        const correlationId =
            this.extractCorrelationId(properties)
            ?? this.correlationService.getCurrentCorrelationId();

        delete properties.correlationId;
        delete properties.CorrelationId;

        if (correlationId) {
            properties.correlationId = correlationId;
        }

        if (entry.level === LogLevel.Error) {
            const error = entry.data?.find(d => d instanceof Error) as Error | undefined;

            if (error) {
                this.appInsights.trackException(error, properties);
                return;
            }
        }

        this.appInsights.trackTrace(
            entry.message,
            this.toSeverityLevel(entry.level),
            properties
        );
    }

    private addDataToProperties(properties: Record<string, string>, data?: unknown[]): void {
        if (!data?.length) {
            return;
        }

        data.forEach((item, index) => {
            if (item == null) {
                return;
            }

            if (item instanceof Error) {
                properties[`error${index}`] = item.message;
                properties[`errorStack${index}`] = item.stack ?? '';
                return;
            }

            if (this.isPlainObject(item)) {
                Object.entries(item).forEach(([key, value]) => {
                    properties[key] = this.toPropertyValue(value);
                });

                return;
            }

            properties[`data${index}`] = this.toPropertyValue(item);
        });

        properties.data = this.safeStringify(data);
    }

    private extractCorrelationId(properties: Record<string, string>): string | undefined {
        return properties.CorrelationId
            || properties.correlationId
            || undefined;
    }

    private isPlainObject(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object'
            && value !== null
            && !Array.isArray(value)
            && !(value instanceof Date)
            && !(value instanceof Error);
    }

    private toPropertyValue(value: unknown): string {
        if (value == null) {
            return '';
        }

        if (value instanceof Date) {
            return value.toISOString();
        }

        if (typeof value === 'object') {
            return this.safeStringify(value);
        }

        return String(value);
    }

    private toSeverityLevel(level: LogLevel): SeverityLevel {
        switch (level) {
            case LogLevel.Debug:
                return SeverityLevel.Verbose;

            case LogLevel.Info:
                return SeverityLevel.Information;

            case LogLevel.Warn:
                return SeverityLevel.Warning;

            case LogLevel.Error:
                return SeverityLevel.Error;

            default:
                return SeverityLevel.Information;
        }
    }

    private shouldLog(level: LogLevel): boolean {
        return level >= this.minLevel && this.minLevel !== LogLevel.Off;
    }

    private writeToConsole(entry: LogEntry): void {
        if (!this.logToConsole) return;

        const prefix = `[${entry.timestampUtc}] [${entry.levelName}] ${entry.message}`;

        // Strip out the Correlation Id when writing to the console.
        const data = (entry.data ?? []).filter(item =>
            !(item &&
                typeof item === 'object' &&
                'correlationId' in (item as Record<string, unknown>))
        );

        switch (entry.level) {
            case LogLevel.Debug:
                console.log(prefix, ...data);
                break;

            case LogLevel.Info:
                console.info(prefix, ...data);
                break;

            case LogLevel.Warn:
                console.warn(prefix, ...data);
                break;

            case LogLevel.Error:
                console.error(prefix, ...data);
                break;
        }
    }

    private parseLogLevel(value: string): LogLevel {
        switch ((value || '').toLowerCase()) {
            case 'debug':
                return LogLevel.Debug;

            case 'info':
                return LogLevel.Info;

            case 'warn':
                return LogLevel.Warn;

            case 'error':
                return LogLevel.Error;

            case 'off':
                return LogLevel.Off;

            default:
                return LogLevel.Warn;
        }
    }

    private safeStringify(value: unknown): string {
        try {
            return JSON.stringify(value);
        }
        catch {
            return '[Unable to serialize log data]';
        }
    }
}
